import React, { useState, useEffect, useRef } from "react";
import home from "../../assets/icons/bookfuncionario/turn.png";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../services/firebaseConnection";
import "./matriz.css";

const Matriz = () => {
  const [termoBusca, setTermoBusca] = useState("");
  const [sugestoes, setSugestoes] = useState([]);
  const [funcionarioSelecionado, setFuncionarioSelecionado] = useState(null);
  const [resultados, setResultados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const cursorPosition = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSugestoes([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const buscarSugestoes = async () => {
      try {
        const termo = termoBusca.trim().toUpperCase();

        const q = query(
          collection(db, "funcionarios"),
          where("nome", ">=", termo),
          where("nome", "<=", termo + "\uf8ff"),
          limit(10),
        );

        const snapshot = await getDocs(q);

        const funcionariosAtivos = snapshot.docs
          .map((docItem) => ({
            id: docItem.id,
            ...docItem.data(),
            nome: (docItem.data().nome || "").toUpperCase(),
            funcao: (docItem.data().funcao || "").toUpperCase(),
          }))
          .filter((funcionario) => funcionario.situacao === "ATIVO");

        setSugestoes(funcionariosAtivos);
      } catch (error) {
        console.error("Erro ao buscar sugestões:", error);
      }
    };

    const timer = setTimeout(() => {
      const inputEstaFocado = document.activeElement === inputRef.current;

      if (termoBusca.trim().length >= 3 && inputEstaFocado) {
        buscarSugestoes();
      } else {
        setSugestoes([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [termoBusca]);

  const handleInputChange = (e) => {
    const { value, selectionStart } = e.target;

    cursorPosition.current = selectionStart;

    setTermoBusca(value.toUpperCase());

    if (funcionarioSelecionado) {
      setFuncionarioSelecionado(null);
      setResultados([]);
      setErro(null);
      setSugestoes([]);
    }

    if (value.trim() === "") {
      setFuncionarioSelecionado(null);
      setResultados([]);
      setErro(null);
      setSugestoes([]);
    }
  };

  useEffect(() => {
    if (inputRef.current && termoBusca.length > 0) {
      try {
        inputRef.current.setSelectionRange(
          cursorPosition.current,
          cursorPosition.current,
        );
      } catch (error) {
        console.debug("Erro ao reposicionar cursor:", error);
      }
    }
  }, [termoBusca]);

  const formatarData = (dataOriginal) => {
    if (!dataOriginal) return "--/--/----";

    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dataOriginal)) {
        const [ano, mes, dia] = dataOriginal.split("-");
        return `${dia}/${mes}/${ano}`;
      }

      const dataObj = new Date(dataOriginal);

      if (isNaN(dataObj.getTime())) return "--/--/----";

      return `${String(dataObj.getDate()).padStart(2, "0")}/${String(
        dataObj.getMonth() + 1,
      ).padStart(2, "0")}/${dataObj.getFullYear()}`;
    } catch {
      return "--/--/----";
    }
  };

  const buscarMatrizesDaObra = async (nomeObra) => {
    try {
      const obraRef = doc(db, "obras_atuais", nomeObra);
      const obraSnap = await getDoc(obraRef);

      if (!obraSnap.exists()) {
        return ["matriz_atlas"];
      }

      const dadosObra = obraSnap.data();

      return dadosObra.matrizesAplicadas?.length > 0
        ? dadosObra.matrizesAplicadas
        : ["matriz_atlas"];
    } catch (error) {
      console.error("Erro ao buscar matrizes da obra:", error);
      return ["matriz_atlas"];
    }
  };

  const buscarDetalhesFuncionario = async (funcionarioId) => {
    setCarregando(true);
    setErro(null);
    setSugestoes([]);
    setResultados([]);

    try {
      const funcionarioRef = doc(db, "funcionarios", funcionarioId);
      const funcionarioSnap = await getDoc(funcionarioRef);

      if (!funcionarioSnap.exists()) {
        throw new Error("Funcionário não encontrado");
      }

      const funcionarioData = funcionarioSnap.data();

      if (funcionarioData.situacao !== "ATIVO") {
        throw new Error("Funcionário desativado");
      }

      setFuncionarioSelecionado({
        ...funcionarioData,
        nome: (funcionarioData.nome || "").toUpperCase(),
        funcao: (funcionarioData.funcao || "").toUpperCase(),
        obra: (funcionarioData.obra || "").toUpperCase(),
      });

      const realizadosRef = collection(
        db,
        `funcionarios/${funcionarioId}/treinamentos`,
      );

      const realizadosSnap = await getDocs(realizadosRef);

      const treinamentosRealizados = new Map();

      realizadosSnap.docs.forEach((docTreinamento) => {
        const data = docTreinamento.data();

        const nomeTreinamento = (
          data.treinamentoNome ||
          data.nome ||
          data.tipo ||
          ""
        )
          .toString()
          .trim()
          .toUpperCase();

        if (nomeTreinamento) {
          treinamentosRealizados.set(nomeTreinamento, {
            realizado: true,
            data: data.data || "",
            instrutor: data.avaliador || "",
            cadastradoPor: data.cadastradoPor || "",
          });
        }
      });

      const funcaoNormalizada = funcionarioData.funcao?.toUpperCase().trim();

      if (!funcaoNormalizada) {
        throw new Error("Função não definida para este funcionário");
      }

      const matrizesAplicadas = await buscarMatrizesDaObra(
        funcionarioData.obra?.toUpperCase().trim(),
      );

      let treinamentosDaFuncao = [];

      for (const matriz of matrizesAplicadas) {
        const treinamentosRef = collection(
          db,
          "matrizes",
          matriz,
          "funcoes",
          funcaoNormalizada,
          "treinamentos",
        );

        const treinamentosSnap = await getDocs(treinamentosRef);

        treinamentosSnap.docs.forEach((docTreinamento) => {
          treinamentosDaFuncao.push({
            id: docTreinamento.id,
            matriz,
            ...docTreinamento.data(),
          });
        });
      }

      if (treinamentosDaFuncao.length === 0) {
        throw new Error("Nenhum treinamento encontrado para esta função");
      }

      processarResultados(treinamentosDaFuncao, treinamentosRealizados);
    } catch (error) {
      console.error("Erro ao buscar detalhes:", error);
      setErro(error.message);
    } finally {
      setCarregando(false);
    }
  };

  const processarResultados = (
    treinamentosDaFuncao,
    treinamentosRealizados,
  ) => {
    const resultadosFormatados = treinamentosDaFuncao.map((treinamentoData) => {
      const nomeTreinamento = (
        treinamentoData.nome ||
        treinamentoData.treinamento ||
        treinamentoData.id
      )
        .toString()
        .trim()
        .toUpperCase();

      const tipo =
        treinamentoData.tipo === "EVENTUAL" ||
        treinamentoData.tipo === "eventual"
          ? "EVENTUAL"
          : "OBRIGATÓRIO";

      const realizadoInfo = treinamentosRealizados.get(nomeTreinamento);

      const status = realizadoInfo ? "REALIZADO" : "PENDENTE";

      return {
        nr: nomeTreinamento,
        tipo,
        status,
        dataTreinamento: realizadoInfo?.data
          ? formatarData(realizadoInfo.data)
          : "--/--/----",
        instrutor: realizadoInfo?.instrutor || "--",
        cadastradoPor: realizadoInfo?.cadastradoPor || "--",
      };
    });

    const resultadosOrdenados = resultadosFormatados.sort((a, b) => {
      if (a.tipo === b.tipo) return a.nr.localeCompare(b.nr);
      return a.tipo === "OBRIGATÓRIO" ? -1 : 1;
    });

    setResultados(resultadosOrdenados);
  };

  const limparBusca = () => {
    setTermoBusca("");
    setFuncionarioSelecionado(null);
    setResultados([]);
    setErro(null);
    setSugestoes([]);

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className="container">
      <div className="busca-container" ref={dropdownRef}>
        <div className="search-wrapper">
          <img
            className="icon-home-matriz"
            onClick={() => navigate("/main")}
            src={home}
            alt="Voltar"
          />

          <input
            ref={inputRef}
            type="text"
            value={termoBusca}
            onChange={handleInputChange}
            placeholder="DIGITE O NOME DO COLABORADOR"
            className="campo-busca"
          />

          {termoBusca.length > 0 && (
            <button onClick={limparBusca} className="limpar-busca">
              ×
            </button>
          )}
        </div>

        {sugestoes.length > 0 && (
          <ul className="sugestoes-lista">
            {sugestoes.map((func) => (
              <li
                key={func.id}
                onClick={() => {
                  setTermoBusca(func.nome);
                  setSugestoes([]);
                  buscarDetalhesFuncionario(func.id);

                  if (inputRef.current) {
                    inputRef.current.blur();
                  }
                }}
                className="sugestao-item"
              >
                <strong>{func.nome}</strong> ({func.cpf}) - {func.funcao}
              </li>
            ))}
          </ul>
        )}
      </div>

      {carregando && <p className="mensagem-status">Carregando...</p>}
      {erro && <p className="mensagem-erro">{erro}</p>}

      {funcionarioSelecionado && (
        <div className="resultado-container">
          <div className="funcionario-info">
            <h3>{funcionarioSelecionado.nome}</h3>

            <div className="funcionario-detalhes">
              <p>
                <strong>CPF:</strong> {funcionarioSelecionado.cpf}
              </p>

              <p>
                <strong>OBRA:</strong> {funcionarioSelecionado.obra}
              </p>

              <p>
                <strong>FUNÇÃO:</strong> {funcionarioSelecionado.funcao}
              </p>
            </div>
          </div>

          <table className="treinamentos-table">
            <thead>
              <tr>
                <th>TREINAMENTO</th>
                <th>TIPO</th>
                <th>DATA</th>
                <th>INSTRUTOR</th>
                <th>LANÇAMENTO</th>
                <th>STATUS</th>
              </tr>
            </thead>

            <tbody>
              {resultados.length > 0
                ? resultados.map((item, index) => (
                    <tr
                      key={index}
                      className={
                        item.status === "PENDENTE" ? "pendente" : "realizado"
                      }
                    >
                      <td>{item.nr}</td>
                      <td>{item.tipo}</td>
                      <td>{item.dataTreinamento}</td>
                      <td>{item.instrutor.toUpperCase()}</td>
                      <td>{item.cadastradoPor}</td>
                      <td>
                        <span
                          className={`status-badge ${item.status.toLowerCase()}`}
                        >
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                : !erro && (
                    <tr>
                      <td colSpan="6">
                        Nenhum treinamento encontrado para esta função.
                      </td>
                    </tr>
                  )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Matriz;
